import React from 'react';
import { ChartClickInteraction } from '@components/Widgets/types';
import { AvTooltip } from '@fabric-ui/components';
import { AvalorTableIds, FormatterType, HeadCellType, isNumberType } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { Box } from '@mui/material';
import { FormattingRule } from '../../../../FormattingRules/types';
import { getColumnFormatter, getRowStyle } from '../../../CustomDashboardsUtils';
import { FieldStyleType } from '../../../types/types';

export const applyRowStyleForTable =
  ({
    selected,
    cell,
    formattingRuleById,
    cellFieldStyle,
    hasClickInteraction = false,
    getRow = row => row,
    fieldMap,
  }: {
    selected: any[];
    cell: HeadCellType;
    formattingRuleById: FormattingRule | undefined;
    cellFieldStyle: FieldStyleType | undefined;
    hasClickInteraction?: boolean;
    getRow?: (row: any) => any;
    fieldMap?: Record<string, any>;
  }) =>
  ({ row }) =>
    getRowStyle({
      selected,
      cell,
      row: getRow(row),
      hasClickInteraction,
      columnFormatterType: cellFieldStyle?.columnFormatter,
      formattingRule: formattingRuleById,
      fieldMap,
    });

export const FormatterWithDrillDown = ({
  disabled,
  nextDrillDownDisplayName,
  cell,
  value,
  rows,
  dimId,
  onClick,
  cellFieldStyle,
  formattingRuleById,
}: {
  disabled: boolean;
  nextDrillDownDisplayName: string;
  cell: HeadCellType;
  value: string | number;
  rows: Record<string, any>[];
  dimId: string;
  onClick: (value: string | number, rows: Record<string, any>[], dimId: string) => void;
  cellFieldStyle?: FieldStyleType;
  formattingRuleById?: FormattingRule;
}) => (
  <AvTooltip disabled={disabled} title={`Drill Down to ${nextDrillDownDisplayName}`}>
    <Box
      sx={{
        ...(isNumberType(cell.type) ? { ...flex.row, flexDirection: 'row-reverse' } : flex.row),
        height: '100%',
        width: '100%',
      }}
      onClick={() => onClick(value, rows, dimId)}>
      {getColumnFormatter({
        value,
        type: cell.type,
        columnFormatter: cellFieldStyle?.columnFormatter,
        formattingRule: formattingRuleById,
      })}
    </Box>
  </AvTooltip>
);

export const FormatterWithClickInteractions = ({
  clickInteractions,
  cell,
  currentIndex,
  data,
  cellFieldStyle,
  formattingRuleById,
  value,
  row,
  originalValue,
  headCellType,
}: {
  clickInteractions: ChartClickInteraction[];
  cell: HeadCellType;
  currentIndex?: number;
  data: Record<string, any>[];
  cellFieldStyle?: FieldStyleType;
  formattingRuleById?: FormattingRule;
  value: string | number;
  row: Record<string, any>;
  originalValue?: string;
  headCellType?: string;
}) => (
  <AvTooltip title={`Click to ${clickInteractions[0]?.title.toLowerCase() ?? ''}`}>
    <Box
      sx={isNumberType(cell.type) ? { ...flex.row, flexDirection: 'row-reverse' } : flex.row}
      onClick={e =>
        clickInteractions[0]?.onClick({
          row,
          cell,
          currentIndex,
          e,
          widgetData: data,
          originalValue,
          headCellType,
        })
      }>
      {getColumnFormatter({
        value,
        type: cell.type,
        columnFormatter: cellFieldStyle?.columnFormatter,
        formattingRule: formattingRuleById,
      })}
    </Box>
  </AvTooltip>
);

export const FormatterDefault = ({
  value,
  cell,
  cellFieldStyle,
  formattingRuleById,
}: {
  value: string | number;
  cell: HeadCellType;
  cellFieldStyle?: FieldStyleType;
  formattingRuleById?: FormattingRule;
}) => (
  <AvTooltip>
    {getColumnFormatter({
      value,
      type: cell.type,
      fieldName: cell.fieldName || cell.id,
      columnFormatter: cellFieldStyle?.columnFormatter,
      formattingRule: formattingRuleById,
    })}
  </AvTooltip>
);

export const getTableTotalProps = (visibleHeadCells: HeadCellType[], responseTotals: Record<string, any> | undefined) => {
  if (!responseTotals) {
    return {};
  }

  return {
    row: visibleHeadCells.reduce(
      (acc, { id }) => ({ ...acc, [id]: id === AvalorTableIds.ExpandCaretId ? '' : (responseTotals[id] ?? null) }),
      {}
    ),
    cells: visibleHeadCells.map(({ id, type }) => ({
      id,
      type: type === FormatterType.date ? 'string' : type,
    })),
  };
};
